/**
 * Respond to Counter Dialog — DSA's response when the lead owner counters their
 * marketplace enquiry.
 *
 * Used by: dsa-offers/ReceivedInvites (DSA reviewing the lead owner's counter)
 *
 * Mirror of ReviewInterestDialog, but from the DSA's perspective:
 *   - Subject is the LEAD OWNER (countered), not the agent
 *   - The "what" is what the DSA will PAY, not earn (neutral band + orange
 *     numeral — green would wrongly imply gain)
 *   - Actions: Accept counter / Negotiate (new proposal) / Withdraw
 *
 * Backend payload (MarketplaceInterestController::formatInterestDetailResponse):
 *   interest.{id, status, status_label, status_severity, status_icon}
 *   interest.{proposed_percentage, counter_percentage, counter_message}
 *   interest.{negotiation_round, is_agent_turn, expires_at}
 *   interest.lead_owner.{id, name}             (detail endpoint, agent viewer only)
 *   interest.lead.{id, borrower_name, product_type, loan_amount, city, state, quality_grade}
 *   interest.negotiations[] + available_actions[]
 *
 * Sizing density matches DsaOfferCard.js (canonical v2.0). No `<style>` blocks.
 * No inline fontSize for text — sub-text-xs icons only via inline fontSize.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Inplace, InplaceDisplay, InplaceContent } from 'primereact/inplace';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import {
    marketplaceService,
    getInterestStatusConfig,
    formatLoanAmount,
    formatTimeAgo,
} from '../../../services/marketplaceService';

const MSG_PREVIEW_CHARS = 140;

// ── helpers (shared with ReviewInterestDialog idiom) ───────────────────────

// Rounds to whole rupees below ₹1L to avoid JS float-precision artefacts
// (e.g. 300000 * 0.07 / 100 = 210.00000000000003).
const formatRupees = (n) => {
    if (n == null || isNaN(Number(n))) return null;
    const v = Number(n);
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
    if (v >= 100000)   return `₹${(v / 100000).toFixed(2)} L`;
    return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const trim3 = (n) => parseFloat(Number(n || 0).toFixed(3));

const sevColor = (s) => ({
    success: 'green', danger: 'red', warning: 'orange',
    info: 'blue',     help: 'purple',
}[s] || 'gray');

const actionMeta = (item) => {
    const t = item?.action_type;
    if (t === 'accepted')           return { severity: 'success', icon: 'pi pi-check',  label: 'Accepted'  };
    if (t === 'rejected')           return { severity: 'danger',  icon: 'pi pi-times',  label: 'Declined'  };
    if (t === 'counter_offer')      return { severity: 'warning', icon: 'pi pi-sync',   label: 'Counter'   };
    if (t === 'interest_expressed') return { severity: 'info',    icon: 'pi pi-star',   label: 'Interest'  };
    if (t === 'new_proposal')       return { severity: 'help',    icon: 'pi pi-send',   label: 'Proposal'  };
    if (t === 'counter_accepted')   return { severity: 'success', icon: 'pi pi-check',  label: 'Counter accepted' };
    if (t === 'withdrawn')          return { severity: 'secondary', icon: 'pi pi-undo', label: 'Withdrawn' };
    return { severity: 'secondary', icon: 'pi pi-circle', label: (t || 'Update').replace(/_/g, ' ') };
};

const gradeSeverity = (g) => {
    if (!g) return null;
    const c = String(g).toUpperCase().charAt(0);
    if (c === 'A') return 'success';
    if (c === 'B') return 'info';
    if (c === 'C') return 'warning';
    return 'danger';
};

const expiryHint = (isoStr) => {
    if (!isoStr) return null;
    const ms = new Date(isoStr).getTime() - Date.now();
    if (isNaN(ms) || ms <= 0) return { label: 'Expired', severity: 'danger' };
    const days  = Math.floor(ms / 86400000);
    const hours = Math.floor(ms / 3600000);
    if (days >= 2)  return { label: `Expires in ${days}d`, severity: 'secondary' };
    if (days === 1) return { label: 'Expires tomorrow',     severity: 'warning'   };
    if (hours >= 2) return { label: `Expires in ${hours}h`, severity: 'warning'   };
    return              { label: 'Expires soon',            severity: 'danger'    };
};

// ── component ──────────────────────────────────────────────────────────────

const RespondToCounterDialog = ({ visible, onHide, interest, onSuccess }) => {
    const toast = useRef(null);

    const [actionChoice,    setActionChoice]    = useState('accept');
    const [newPct,          setNewPct]          = useState(3.0);
    const [proposalMessage, setProposalMessage] = useState('');
    const [submitting,      setSubmitting]      = useState(false);
    const [negotiations,    setNegotiations]    = useState([]);
    const [negotiationsLoading, setNegotiationsLoading] = useState(false);

    // ── derived ────────────────────────────────────────────────────────────
    const owner       = interest?.lead_owner || {};
    const ownerName   = owner.name || 'Lead owner';
    const ownerAvatar = owner.avatar; // not yet shipped server-side — graceful

    const lead         = interest?.lead || null;
    const leadAmount   = lead?.loan_amount ?? null;
    const proposedPct  = trim3(interest?.proposed_percentage);
    const counterPct   = trim3(interest?.counter_percentage);
    const gradeSev     = gradeSeverity(lead?.quality_grade);

    const counterRupees  = leadAmount != null ? formatRupees(leadAmount * counterPct / 100)  : null;

    // Cost delta vs DSA's original offer (positive = LO is asking more)
    const costDelta = trim3(counterPct - proposedPct);
    const costDeltaRupees = leadAmount != null && Math.abs(costDelta) >= 0.001
        ? formatRupees(Math.abs(leadAmount * costDelta / 100))
        : null;

    // New proposal delta vs LO's counter (drives CTA + helper text)
    const proposalDelta = trim3(newPct - counterPct);

    const expiry       = useMemo(() => expiryHint(interest?.expires_at), [interest?.expires_at]);
    const statusConfig = getInterestStatusConfig(interest?.status);

    const availableActions = interest?.available_actions || [];
    const canRespond = availableActions.length > 0
        ? availableActions.some((a) => ['accept_counter', 'new_proposal', 'withdraw'].includes(a))
        : (interest?.is_agent_turn || interest?.status === 'countered');

    const showHistory = (interest?.negotiation_round || 0) > 1 || negotiations.length > 1;

    // ── fetch history ──────────────────────────────────────────────────────
    const fetchNegotiationHistory = useCallback(async () => {
        if (!interest?.id) return;
        setNegotiationsLoading(true);
        try {
            const response = await marketplaceService.getInterestDetails(interest.id);
            if (response?.success) setNegotiations(response.data?.negotiations || []);
        } catch (e) {
            console.error('Failed to fetch negotiation history:', e?.message || e);
        } finally {
            setNegotiationsLoading(false);
        }
    }, [interest?.id]);

    useEffect(() => {
        if (!visible || !interest) return;
        setActionChoice('accept');
        // Default new proposal to LO's counter % — the negotiated value on this
        // round is the natural starting point (same pattern as the LO Counter panel).
        setNewPct(Number(interest.counter_percentage) || 3.0);
        setProposalMessage('');
        fetchNegotiationHistory();
    }, [visible, interest, fetchNegotiationHistory]);

    // ── handlers ───────────────────────────────────────────────────────────
    const handleAcceptCounter = () => {
        confirmDialog({
            message: counterRupees
                ? `Accept and pay ${counterRupees} (${counterPct}%) on disbursal?`
                : `Accept the counter offer of ${counterPct}% lead commission?`,
            header: 'Confirm accept',
            icon: 'pi pi-check-circle',
            acceptClassName: 'p-button-success',
            accept: async () => {
                setSubmitting(true);
                try {
                    const response = await marketplaceService.respondToCounter(interest.id, {
                        action: 'accept',
                        message: 'Counter offer accepted',
                    });
                    if (response?.success) {
                        toast.current?.show({ severity: 'success', summary: 'Accepted', detail: 'Collaboration is now active.' });
                        setTimeout(() => { onHide(); onSuccess?.(response.data); }, 300);
                    }
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to accept counter' });
                } finally { setSubmitting(false); }
            },
        });
    };

    const handleNewProposal = async () => {
        // Exactly matching LO's counter = accept it.
        if (Math.abs(proposalDelta) < 0.001) {
            handleAcceptCounter();
            return;
        }
        setSubmitting(true);
        try {
            const response = await marketplaceService.respondToCounter(interest.id, {
                action: 'new_proposal',
                newPercentage: newPct,
                message: proposalMessage.trim() || undefined,
            });
            if (response?.success) {
                toast.current?.show({ severity: 'success', summary: 'Proposal sent', detail: 'Waiting for owner response.' });
                onSuccess?.(response.data);
                onHide();
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to send proposal' });
        } finally { setSubmitting(false); }
    };

    const handleWithdraw = () => {
        confirmDialog({
            message: 'Withdraw your enquiry on this lead? You can send a new enquiry later if the lead is still available.',
            header: 'Confirm withdraw',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setSubmitting(true);
                try {
                    const response = await marketplaceService.withdrawInterest(interest.id, {
                        reason: 'Withdrawn by agent',
                    });
                    if (response?.success) {
                        toast.current?.show({ severity: 'info', summary: 'Withdrawn', detail: 'Your enquiry has been withdrawn.' });
                        onSuccess?.(response.data);
                        onHide();
                    }
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to withdraw' });
                } finally { setSubmitting(false); }
            },
        });
    };

    if (!interest) return null;

    // ── header ─────────────────────────────────────────────────────────────
    const dialogHeader = (
        <div className="flex align-items-center gap-2 flex-wrap">
            <Tag
                value={statusConfig.label || 'Countered'}
                severity={statusConfig.severity || 'warning'}
                icon={statusConfig.icon ? `pi ${statusConfig.icon}` : 'pi pi-sync'}
                className="text-xs"
            />
            <span className="text-900 font-semibold text-sm">Counter received</span>
            {expiry && (
                <Tag value={expiry.label} severity={expiry.severity} icon="pi pi-clock" className="text-xs" />
            )}
            {interest.negotiation_round > 1 && (
                <Tag value={`Round ${interest.negotiation_round}`} severity="secondary" className="text-xs ml-auto" />
            )}
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={onHide}
                header={dialogHeader}
                modal
                draggable={false}
                closable={!submitting}
                closeOnEscape={!submitting}
                style={{ width: '90vw', maxWidth: '480px' }}
                pt={{
                    content: { className: 'p-3' },
                    header:  { className: 'px-3 py-2' },
                }}
            >
                <div className="flex flex-column gap-3">

                    {/* ── 1. Lead owner identity (WHO countered) ──────── */}
                    <div className="surface-card border-round border-1 surface-border p-3">
                        <div className="flex align-items-center gap-2">
                            <Avatar
                                image={ownerAvatar}
                                label={ownerName.charAt(0).toUpperCase()}
                                shape="circle"
                                className="bg-primary text-white flex-shrink-0"
                                style={{ width: '2.25rem', height: '2.25rem', fontSize: '0.9rem' }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-900 text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {ownerName}
                                </div>
                                <div className="text-500 text-xs">countered your enquiry</div>
                            </div>
                        </div>
                    </div>

                    {/* ── 2. Counter band — neutral, orange numeral ───── */}
                    <div className="surface-100 border-round border-1 surface-border p-3 text-center">
                        <div className="text-600 text-xs font-medium">Owner&rsquo;s counter</div>
                        <div className="text-orange-600 font-bold text-base mt-1">
                            {counterRupees || `${counterPct}%`}
                        </div>
                        {counterRupees && (
                            <div className="text-600 text-xs mt-1">
                                {counterPct}% on {formatLoanAmount(leadAmount)} disbursal
                            </div>
                        )}
                        {Math.abs(costDelta) >= 0.001 && costDeltaRupees && (
                            <div className={`text-xs font-medium mt-1 ${costDelta > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                {costDelta > 0 ? '+' : '−'}{costDeltaRupees} {costDelta > 0 ? 'more than' : 'less than'} you offered ({proposedPct}%)
                            </div>
                        )}
                    </div>

                    {/* ── 3. Lead context ─────────────────────────────── */}
                    {lead && (
                        <div className="surface-50 border-round border-1 surface-border p-3">
                            <div className="flex align-items-start gap-2">
                                <i className="pi pi-user text-primary flex-shrink-0 mt-1" style={{ fontSize: '0.8rem' }} aria-hidden="true" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex align-items-center gap-2">
                                        <span className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis flex-1 min-w-0">
                                            {lead.borrower_name || 'Lead'}
                                        </span>
                                        {lead.quality_grade && gradeSev && (
                                            <Tag
                                                value={`Grade ${String(lead.quality_grade).toUpperCase()}`}
                                                severity={gradeSev}
                                                className="text-xs flex-shrink-0"
                                            />
                                        )}
                                    </div>
                                    <div className="flex align-items-center gap-1 flex-wrap text-xs text-600 mt-1">
                                        {lead.product_type && (
                                            <span className="flex align-items-center gap-1">
                                                <i className="pi pi-tag" style={{ fontSize: '0.65rem' }} aria-hidden="true" />
                                                {lead.product_type}
                                            </span>
                                        )}
                                        {lead.loan_amount != null && (
                                            <>
                                                <span className="text-400">·</span>
                                                <span className="font-semibold text-700">{formatLoanAmount(lead.loan_amount)}</span>
                                            </>
                                        )}
                                        {(lead.city || lead.state) && (
                                            <>
                                                <span className="text-400">·</span>
                                                <span className="flex align-items-center gap-1">
                                                    <i className="pi pi-map-marker" style={{ fontSize: '0.65rem' }} aria-hidden="true" />
                                                    {[lead.city, lead.state].filter(Boolean).join(', ')}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── 4. Owner's message ──────────────────────────── */}
                    {interest.counter_message && (
                        <div className="surface-card border-round border-1 surface-border border-left-3 border-primary p-3">
                            {interest.counter_message.length <= MSG_PREVIEW_CHARS ? (
                                <div className="flex align-items-start gap-2">
                                    <i className="pi pi-comment text-primary mt-1" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                    <div className="text-700 text-xs line-height-3 flex-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        &ldquo;{interest.counter_message}&rdquo;
                                    </div>
                                </div>
                            ) : (
                                <Inplace closable closeIcon="pi pi-chevron-up">
                                    <InplaceDisplay>
                                        <div className="flex align-items-start gap-2">
                                            <i className="pi pi-comment text-primary mt-1" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                            <div className="text-700 text-xs line-height-3 flex-1">
                                                &ldquo;{interest.counter_message.slice(0, MSG_PREVIEW_CHARS).trim()}&hellip;&rdquo;{' '}
                                                <span className="text-primary font-medium">Read more</span>
                                            </div>
                                        </div>
                                    </InplaceDisplay>
                                    <InplaceContent>
                                        <div className="flex align-items-start gap-2">
                                            <i className="pi pi-comment text-primary mt-1" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                            <div className="text-700 text-xs line-height-3 flex-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                &ldquo;{interest.counter_message}&rdquo;
                                            </div>
                                        </div>
                                    </InplaceContent>
                                </Inplace>
                            )}
                        </div>
                    )}

                    {/* ── 5+6. Action — 3 colour-coded buttons + panel ── */}
                    {canRespond ? (
                        <>
                            <div className="flex gap-2">
                                <Button
                                    label="Accept"
                                    icon="pi pi-check"
                                    severity="success"
                                    outlined={actionChoice !== 'accept'}
                                    size="small"
                                    className="flex-1"
                                    onClick={() => setActionChoice('accept')}
                                />
                                <Button
                                    label="Negotiate"
                                    icon="pi pi-sync"
                                    severity="warning"
                                    outlined={actionChoice !== 'negotiate'}
                                    size="small"
                                    className="flex-1"
                                    onClick={() => setActionChoice('negotiate')}
                                />
                                <Button
                                    label="Withdraw"
                                    icon="pi pi-times"
                                    severity="danger"
                                    outlined={actionChoice !== 'withdraw'}
                                    size="small"
                                    className="flex-1"
                                    onClick={() => setActionChoice('withdraw')}
                                />
                            </div>

                            {actionChoice === 'accept' && (
                                <div className="surface-50 border-round border-1 surface-border p-3 flex flex-column gap-2 align-items-center text-center">
                                    <div className="text-700 text-xs">
                                        Accept the counter.
                                        {counterRupees && (
                                            <> You&rsquo;ll pay <strong className="text-orange-600">{counterRupees}</strong> on disbursal.</>
                                        )}
                                    </div>
                                    <Button
                                        label="Confirm and accept"
                                        icon="pi pi-check"
                                        severity="success"
                                        size="small"
                                        className="w-full"
                                        onClick={handleAcceptCounter}
                                        loading={submitting}
                                    />
                                </div>
                            )}

                            {actionChoice === 'negotiate' && (
                                <div className="surface-50 border-round border-1 surface-border p-3 flex flex-column gap-3">
                                    <div className="text-center">
                                        <div className="text-500 text-xs font-medium uppercase mb-2">
                                            Your new proposal
                                        </div>
                                        <InputNumber
                                            value={newPct}
                                            onValueChange={(e) => setNewPct(Math.min(10, Math.max(0.5, e.value ?? 0.5)))}
                                            mode="decimal"
                                            minFractionDigits={1}
                                            maxFractionDigits={3}
                                            min={0.5}
                                            max={10}
                                            suffix="%"
                                            inputClassName="text-center font-bold text-orange-600 text-base w-7rem"
                                        />
                                    </div>

                                    {leadAmount != null && (
                                        <div className="text-center">
                                            <div className="text-xs text-700">
                                                You&rsquo;d pay{' '}
                                                <span className="font-bold text-orange-600">
                                                    {formatRupees(leadAmount * newPct / 100)}
                                                </span>
                                            </div>
                                            {Math.abs(proposalDelta) >= 0.001 ? (
                                                <div className={`text-xs font-medium mt-1 ${proposalDelta > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                                    {proposalDelta > 0 ? '+' : '−'}{formatRupees(Math.abs(leadAmount * proposalDelta / 100))} {proposalDelta > 0 ? 'more than' : 'less than'} owner&rsquo;s counter
                                                </div>
                                            ) : (
                                                <div className="text-xs text-500 mt-1">Same as owner&rsquo;s counter — adjust to send</div>
                                            )}
                                        </div>
                                    )}

                                    <InputTextarea
                                        value={proposalMessage}
                                        onChange={(e) => setProposalMessage(e.target.value)}
                                        rows={2}
                                        autoResize
                                        className="w-full text-xs"
                                        placeholder="Add a note to your proposal (optional)"
                                        maxLength={500}
                                    />

                                    <Button
                                        label="Send new proposal"
                                        icon="pi pi-send"
                                        severity="warning"
                                        className="w-full"
                                        onClick={handleNewProposal}
                                        loading={submitting}
                                        disabled={Math.abs(proposalDelta) < 0.001}
                                    />
                                </div>
                            )}

                            {actionChoice === 'withdraw' && (
                                <div className="surface-50 border-round border-1 surface-border p-3 flex flex-column gap-2">
                                    <Message
                                        severity="warn"
                                        text="Withdrawing is permanent. You can send a new enquiry later if the lead is still available."
                                        className="w-full"
                                    />
                                    <Button
                                        label="Withdraw enquiry"
                                        icon="pi pi-times"
                                        severity="danger"
                                        outlined
                                        size="small"
                                        className="w-full"
                                        onClick={handleWithdraw}
                                        loading={submitting}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="surface-card border-round border-1 surface-border p-2 flex align-items-center gap-2">
                            <i
                                className={`pi ${statusConfig.icon || 'pi-info-circle'} text-${sevColor(statusConfig.severity)}-500`}
                                style={{ fontSize: '1rem' }}
                                aria-hidden="true"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-900 font-semibold text-sm">{statusConfig.label}</div>
                                <div className="text-500 text-xs">
                                    {interest.status === 'accepted'  && 'Your enquiry has been accepted.'}
                                    {interest.status === 'rejected'  && 'Your enquiry was declined by the owner.'}
                                    {interest.status === 'pending'   && 'Waiting for the owner to respond.'}
                                    {interest.status === 'withdrawn' && 'You withdrew this enquiry.'}
                                    {interest.status === 'expired'   && 'This enquiry has expired.'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── 7. Past rounds — only if rounds > 1 ──────────── */}
                    {showHistory && (
                        <Accordion>
                            <AccordionTab
                                header={(
                                    <span className="flex align-items-center gap-2 text-xs font-medium">
                                        <i className="pi pi-history text-primary" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                        Past rounds
                                        {negotiationsLoading
                                            ? <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '0.7rem' }} aria-hidden="true" />
                                            : <Tag value={negotiations.length} severity="info" className="text-xs" />
                                        }
                                    </span>
                                )}
                            >
                                {negotiationsLoading ? (
                                    <div className="flex flex-column gap-2">
                                        {[1, 2].map((i) => (
                                            <div key={i} className="flex justify-content-end">
                                                <Skeleton width="70%" height="3rem" className="border-round" />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* WhatsApp-style chat: DSA view → non-owner = You (right) */
                                    <div className="flex flex-column gap-2">
                                        {negotiations.map((item, idx) => {
                                            const meta = actionMeta(item);
                                            const isYou = item.actor_role !== 'owner';
                                            const actorLabel = isYou ? 'You' : (item.actor_name || 'Owner');
                                            const itemPct = item.proposed_percentage != null ? trim3(item.proposed_percentage) : null;
                                            return (
                                                <div key={idx} className={`flex ${isYou ? 'justify-content-end' : 'justify-content-start'}`}>
                                                    <div
                                                        className={`border-round p-2 ${isYou ? 'bg-primary-50' : 'surface-100'}`}
                                                        style={{ maxWidth: '85%' }}
                                                    >
                                                        <div className="flex align-items-center gap-1 flex-wrap mb-1">
                                                            <span className="text-900 font-medium text-xs">{actorLabel}</span>
                                                            <Tag value={meta.label} severity={meta.severity} className="text-xs" />
                                                            {itemPct != null && (
                                                                <span className="text-700 text-xs font-semibold">{itemPct}%</span>
                                                            )}
                                                        </div>
                                                        {item.message && (
                                                            <div className="text-700 text-xs mb-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                &ldquo;{item.message}&rdquo;
                                                            </div>
                                                        )}
                                                        <div className="text-400 text-xs text-right white-space-nowrap">
                                                            {item.time_ago || formatTimeAgo(item.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </AccordionTab>
                        </Accordion>
                    )}
                </div>
            </Dialog>
            <ConfirmDialog />
        </>
    );
};

export default RespondToCounterDialog;
